import 'app/polyfill/native';

import { withReactNavigationAutotrack as _withAutotrack } from '@heap/heap-react-native-autocapture';
import * as SplashScreen from 'expo-splash-screen';
import { Box, NativeBaseProvider } from 'native-base';
import React, { forwardRef, useCallback, useEffect, useState } from 'react';
import { Platform, StatusBar } from 'react-native';
import { QueryClientProvider } from 'react-query';
import { shallow } from 'zustand/shallow';

import AndroidSplashOverlay from 'app/components/AndroidSplashOverlay';
import AppDownloadBanner from 'app/components/AppDownloadBanner';
import AppUpdateDialog from 'app/components/AppUpdateDialog';
import DevToolsFAB from 'app/components/DevToolsFAB';
import EntitlementSwitcher from 'app/components/EntitlementSwitcher';
import ErrorBoundary from 'app/components/ErrorBoundary';
import LocaleChangeOverlay from 'app/components/LocaleChangeOverlay/LocaleChangeOverlay';
import ReserveForGlobalHeader from 'app/components/ReserveForGlobalHeader';
import useAnalytics from 'app/hooks/useAnalytics';
import useBootstrap from 'app/hooks/useBootstrap';
import useColorScheme from 'app/hooks/useColorScheme';
import useLinkHandling from 'app/hooks/useLinkHandling';
import usePushNotifications from 'app/hooks/usePushNotifications';
import useRequestPermissions from 'app/hooks/useRequestPermissions';
import useStore from 'app/hooks/useStore';
import RootNavigator from 'app/navigation/RootNavigator';
import AuthProvider from 'app/providers/AuthProvider';
import GuestProvider from 'app/providers/GuestProvider';
import I18nProvider from 'app/providers/I18nProvider';
import MessagingProvider from 'app/providers/MessagingProvider';
import RenderHTMLProvider from 'app/providers/RenderHTMLProvider';
import { TravelPartyProvider } from 'app/providers/TravelPartyProvider';
import QueryClient from 'app/services/QueryClient';
import theme from 'app/theme';
import { MAX_WIDTH_WEB } from 'app/theme/breakpoints';

const queryClient = QueryClient();

/**
 * Main entry point of the app.
 * Must be a child of <Providers/> to consume all the various contexts.
 */
function Main() {
  const { isReady } = useBootstrap();
  const [isFirstScreenShown, setIsFirstScreenShown] = useState(false);
  // The splash stays up until the first screen (or an update dialog) has mounted, so no blank
  // screen shows in between.
  const isFirstScreenReady = isReady && isFirstScreenShown;
  const onFirstScreenShown = useCallback(() => setIsFirstScreenShown(true), []);
  const colorScheme = useColorScheme();
  const [colorMode, setColorMode] = useStore((s) => [s.colorMode, s.setColorMode], shallow);

  useLinkHandling();
  useAnalytics();
  usePushNotifications();
  useRequestPermissions();

  useEffect(() => {
    if (colorMode !== 'light') setColorMode('light');
  }, [colorScheme, colorMode, setColorMode]);

  useEffect(() => {
    // on Android the native splash is hidden by AndroidSplashOverlay once its image is shown
    if (isFirstScreenReady && Platform.OS !== 'android') SplashScreen.hideAsync();
  }, [isFirstScreenReady]);

  return (
    <>
      {isReady && <App colorMode={colorMode} onReady={onFirstScreenShown} />}
      <AndroidSplashOverlay isReady={isFirstScreenReady} />
    </>
  );
}

function App({ colorMode, onReady }: { colorMode: 'light' | 'dark'; onReady: () => void }) {
  return (
    <AppUpdateDialog onShow={onReady}>
      <LocaleChangeOverlay>
        <RootContainer>
          <RootNavigator
            before={
              Platform.OS === 'web' && (
                <>
                  <ReserveForGlobalHeader />
                  <AppDownloadBanner />
                </>
              )
            }
            colorScheme={colorMode}
            onReady={onReady}
          >
            <DevToolsFAB />
          </RootNavigator>
        </RootContainer>
        <EntitlementSwitcher />
        {/* Android is always edge-to-edge: the status bar is transparent and drawn over. */}
        <StatusBar barStyle="dark-content" />
      </LocaleChangeOverlay>
    </AppUpdateDialog>
  );
}

function RootContainer({ children }: { children: JSX.Element }) {
  return Platform.OS === 'web' ? (
    <Box
      _web={{
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore: this is valid web style
        style: {
          maxWidth: MAX_WIDTH_WEB,
          marginLeft: 'auto',
          marginRight: 'auto',
          height: '100%',
          width: '100vw',
        },
      }}
    >
      {children}
    </Box>
  ) : (
    children
  );
}

function Providers() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <GuestProvider>
            <TravelPartyProvider>
              <I18nProvider>
                <MessagingProvider>
                  <NativeBaseProvider theme={theme}>
                    <RenderHTMLProvider>
                      <Main />
                    </RenderHTMLProvider>
                  </NativeBaseProvider>
                </MessagingProvider>
              </I18nProvider>
            </TravelPartyProvider>
          </GuestProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

const ForwardedProviders = forwardRef(Providers);
export default _withAutotrack ? _withAutotrack(ForwardedProviders) : ForwardedProviders;
